/**
 * Collection Facets Web Component
 * Handles AJAX filtering, sorting, and pagination via Shopify Section Rendering API.
 * Progressive enhancement: works as standard link navigation without JS.
 */
import { ABBaseComponent, defineComponent } from './ab-base-component.js';

class ABCollectionFacets extends ABBaseComponent {
  connectedCallback() {
    this.grid = this.$('#ab-product-grid');
    this.facets = this.$('#ab-collection-facets');
    this.activeFilters = this.$('#ab-active-filters');
    this.toolbar = this.$('#ab-collection-toolbar');
    this.sortContainer = this.$('#ab-collection-sort');

    this._abortController = null;
    this._priceDebounce = null;

    this.addEventListener('click', this._onClick.bind(this));
    this.addEventListener('change', this._onChange.bind(this));
    this.addEventListener('input', this._onInput.bind(this));
    this._boundOnPopState = this._onPopState.bind(this);
    window.addEventListener('popstate', this._boundOnPopState);
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this._boundOnPopState);
  }

  _onClick(e) {
    const link = e.target.closest('[data-facet-value], [data-facet-reset], [data-active-filter-remove], [data-active-filter-clear], .ab-pagination a');
    if (!link) return;

    const href = link.getAttribute('href');
    if (!href || href === '#') return;

    e.preventDefault();
    this._navigate(href);
  }

  _onChange(e) {
    const sortSelect = e.target.closest('[data-sort-select]');
    if (!sortSelect) return;

    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sortSelect.value);
    url.searchParams.delete('page');
    this._navigate(url.toString());
  }

  _onInput(e) {
    const priceInput = e.target.closest('[data-facet-price-min], [data-facet-price-max]');
    if (!priceInput) return;

    clearTimeout(this._priceDebounce);
    this._priceDebounce = setTimeout(() => {
      this._applyPriceFilter(priceInput);
    }, 600);
  }

  _applyPriceFilter(input) {
    const range = input.closest('[data-facet-price-range]');
    if (!range) return;

    const minInput = range.querySelector('[data-facet-price-min]');
    const maxInput = range.querySelector('[data-facet-price-max]');
    const url = new URL(window.location.href);

    if (minInput && minInput.name) {
      const minVal = minInput.value;
      if (minVal) {
        url.searchParams.set(minInput.name, (parseFloat(minVal) * 100).toString());
      } else {
        url.searchParams.delete(minInput.name);
      }
    }

    if (maxInput && maxInput.name) {
      const maxVal = maxInput.value;
      if (maxVal) {
        url.searchParams.set(maxInput.name, (parseFloat(maxVal) * 100).toString());
      } else {
        url.searchParams.delete(maxInput.name);
      }
    }

    url.searchParams.delete('page');
    this._navigate(url.toString());
  }

  _onPopState() {
    this._fetchAndReplace(window.location.href, false);
  }

  _navigate(href) {
    this._fetchAndReplace(href, true);
  }

  async _fetchAndReplace(href, pushState) {
    if (this._abortController) {
      this._abortController.abort();
    }
    this._abortController = new AbortController();

    this._setLoading(true);

    try {
      const url = new URL(href, window.location.origin);
      const sectionId = this.sectionId;
      if (!sectionId) return;

      url.searchParams.set('sections', sectionId);

      const res = await fetch(url.toString(), {
        signal: this._abortController.signal,
      });

      if (!res.ok) {
        window.location.href = href;
        return;
      }

      const json = await res.json();
      const html = json[sectionId];
      if (!html) {
        window.location.href = href;
        return;
      }

      const doc = new DOMParser().parseFromString(html, 'text/html');

      this._replaceSection('#ab-product-grid', doc);
      this._replaceSection('#ab-collection-facets', doc);
      this._replaceSection('#ab-active-filters', doc);
      this._replaceSection('#ab-collection-toolbar', doc);
      this._replaceSection('#ab-collection-sort', doc);

      if (pushState) {
        const cleanUrl = new URL(href, window.location.origin);
        cleanUrl.searchParams.delete('sections');
        history.pushState({}, '', cleanUrl.toString());
      }

      this._scrollToGrid();
    } catch (err) {
      if (err.name !== 'AbortError') {
        window.location.href = href;
      }
    } finally {
      this._setLoading(false);
    }
  }

  _replaceSection(selector, doc) {
    const newEl = doc.querySelector(selector);
    const oldEl = this.querySelector(selector);
    if (newEl && oldEl) {
      oldEl.replaceWith(newEl);
    }
  }

  _setLoading(isLoading) {
    const grid = this.$('#ab-product-grid');
    if (grid) {
      grid.classList.toggle('is-loading', isLoading);
    }
  }

  _scrollToGrid() {
    const toolbar = this.$('#ab-collection-toolbar');
    if (toolbar) {
      const top = toolbar.getBoundingClientRect().top + window.scrollY - 100;
      if (window.scrollY > top) {
        window.scrollTo({ top, behavior: 'smooth' });
      }
    }
  }
}

defineComponent('ab-collection-facets', ABCollectionFacets);
